import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

export type PreviewHistoryPage = components["schemas"]["PreviewHistoryPage"];
export type PreviewHistoryItem = components["schemas"]["PreviewHistoryItem"];
export type PreviewHistoryInspectionState =
  components["schemas"]["PreviewHistoryInspectionState"];
export type PreviewHistoryTraceStatus =
  components["schemas"]["PreviewHistoryTraceStatus"];

/**
 * The list endpoint caps a page at 25 because each row confirms one recorded
 * binding. Twenty leaves room below the ceiling and is large enough for the
 * first human scan; every continuation passes the server cursor verbatim.
 */
export const PREVIEW_HISTORY_PAGE_SIZE = 20;

export function usePreviewHistoryQuery(
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseInfiniteQueryResult<InfiniteData<PreviewHistoryPage>> {
  const client = useApiClient();
  return useInfiniteQuery({
    queryKey: queryKeys.previewHistory(projectId),
    initialPageParam: null as string | null,
    async queryFn({ pageParam }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/preview-runs", {
          params: {
            path: { projectId },
            query: {
              limit: PREVIEW_HISTORY_PAGE_SIZE,
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
        }),
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    enabled: enabled && projectId.length > 0,
    // A return visit is a new discovery, not a replay of browser memory. The
    // route also waits for `isFetchedAfterMount` before showing any rows.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    retry: false,
  });
}
